import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useGarage } from "../../context/GarageContext";
import useDebounce from "../../../../../hooks/useDebounce";

export const useGaragePage = () => {
  // ============================================================================
  // External Hooks
  // ============================================================================
  const { contactId } = useParams();
  const {
    baseVehicles,
    baseMeta,
    basePartyUuid,
    vehicles,
    vehiclesMeta,
    makes,
    models,
    years,
    loading,
    filtersLoading,
    error,
    isEmpty,
    fetchVehicles,
    fetchMakes,
    fetchModels,
    fetchYears,
    resetVehiclesToBase,
    resetVehicles,
  } = useGarage();

  // ============================================================================
  // State - Filter States
  // ============================================================================
  const [searchValue, setSearchValue] = useState("");
  const [makeFilter, setMakeFilter] = useState("");
  const [modelFilter, setModelFilter] = useState("");
  const [yearFilter, setYearFilter] = useState("");

  // ============================================================================
  // Debounced Search Value
  // ============================================================================
  const debouncedSearchValue = useDebounce(searchValue, 500);

  // ============================================================================
  // State - Pagination & Sorting
  // ============================================================================
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [orderBy, setOrderBy] = useState(["createdAt"]);
  const [orderDirection, setOrderDirection] = useState(["DESC"]);

  // ============================================================================
  // State - UI State (Keys and Local Searches)
  // ============================================================================
  const [searchKey, setSearchKey] = useState(0);
  const [filtersKey, setFiltersKey] = useState(0);
  const [localSearches, setLocalSearches] = useState({
    make: "",
    model: "",
    year: "",
  });

  // ============================================================================
  // State - Internal State
  // ============================================================================
  const [hasLoadedBase, setHasLoadedBase] = useState(false);
  const prevContactIdRef = useRef(null);
  const prevSanitizedSearchRef = useRef(null);

  // ============================================================================
  // Helper Functions
  // ============================================================================
  const filterOptions = useCallback((options, searchTerm) => {
    if (!searchTerm.trim()) return options;
    const searchLower = searchTerm.toLowerCase();
    return options.filter(
      (option) =>
        option.label?.toLowerCase().includes(searchLower) ||
        option.value?.toLowerCase().includes(searchLower)
    );
  }, []);

  // ============================================================================
  // useMemo - Base Options
  // ============================================================================
  const makeOptions = useMemo(
    () => makes.map((make) => ({ label: make, value: make })),
    [makes]
  );

  const modelOptions = useMemo(
    () => models.map((model) => ({ label: model, value: model })),
    [models]
  );

  const yearOptions = useMemo(
    () => years.map((year) => ({ label: year.toString(), value: year.toString() })),
    [years]
  );

  // ============================================================================
  // useMemo - Filtered Options (for Select.Search)
  // ============================================================================
  const filteredMake = useMemo(
    () => filterOptions(makeOptions, localSearches.make),
    [makeOptions, localSearches.make, filterOptions]
  );

  const filteredModel = useMemo(
    () => filterOptions(modelOptions, localSearches.model),
    [modelOptions, localSearches.model, filterOptions]
  );

  const filteredYear = useMemo(
    () => filterOptions(yearOptions, localSearches.year),
    [yearOptions, localSearches.year, filterOptions]
  );

  // ============================================================================
  // useMemo - Derived Data (for API calls)
  // ============================================================================
  const filtersPayload = useMemo(() => {
    const payload = {};

    if (makeFilter) {
      payload.make = makeFilter;
    }

    if (modelFilter) {
      payload.model = modelFilter;
    }

    if (yearFilter) {
      payload.modelYear = parseInt(yearFilter, 10);
    }

    return Object.keys(payload).length ? payload : undefined;
  }, [makeFilter, modelFilter, yearFilter]);

  const sanitizedSearch = useMemo(() => {
    const trimmed = debouncedSearchValue.trim();
    return trimmed.length ? trimmed : null;
  }, [debouncedSearchValue]);

  // ============================================================================
  // useMemo - Check if there are active filters
  // ============================================================================
  const hasActiveFilters = useMemo(() => {
    return Boolean(
      sanitizedSearch ||
      makeFilter ||
      modelFilter ||
      yearFilter
    );
  }, [sanitizedSearch, makeFilter, modelFilter, yearFilter]);

  // ============================================================================
  // Utility Callbacks
  // ============================================================================
  const resetLocalState = useCallback(() => {
    setSearchValue("");
    setMakeFilter("");
    setModelFilter("");
    setYearFilter("");
    setPage(1);
    setPageSize(20);
    setOrderBy(["createdAt"]);
    setOrderDirection(["DESC"]);
  }, []);

  const resetLocalSearches = useCallback(() => {
    setLocalSearches({
      make: "",
      model: "",
      year: "",
    });
  }, []);

  const updateLocalSearch = useCallback((filterType, value) => {
    setLocalSearches((prev) => ({
      ...prev,
      [filterType]: value,
    }));
  }, []);

  // ============================================================================
  // useEffect Hooks
  // ============================================================================
  useEffect(() => {
    if (!contactId) return;

    // Validate basePartyUuid mismatch (like useAppointmentsSnapshot)
    if (basePartyUuid && basePartyUuid !== contactId) {
      resetVehicles();
    }

    // Validate prevContactIdRef mismatch
    if (prevContactIdRef.current && prevContactIdRef.current !== contactId) {
      resetVehicles();
    }

    prevContactIdRef.current = contactId;
    setHasLoadedBase(false);
    resetLocalState();
    fetchMakes(contactId);
    fetchModels(contactId);
    fetchYears(contactId);
  }, [contactId, basePartyUuid, fetchMakes, fetchModels, fetchYears, resetVehicles, resetLocalState]);

  useEffect(() => {
    if (!contactId) return;

    const hasFilters = Boolean(filtersPayload || sanitizedSearch);
    const hasSorting =
      (orderBy[0] || "") !== "createdAt" || (orderDirection[0] || "") !== "DESC";
    const hasPaginationChanges = page !== 1 || pageSize !== 20;

    // Detectar cuando el search cambia de tener valor a ser null
    const searchWasCleared = 
      prevSanitizedSearchRef.current !== null && 
      sanitizedSearch === null;

    const params = {
      party_uuid: contactId,
      page,
      page_size: pageSize,
      order_by: orderBy,
      order_direction: orderDirection,
      filters: filtersPayload,
      search: sanitizedSearch ? { keyword: sanitizedSearch } : undefined,
    };

    if (!hasFilters && !hasSorting && !hasPaginationChanges) {
      if (!hasLoadedBase) {
        if (baseVehicles.length) {
          resetVehiclesToBase();
          setHasLoadedBase(true);
          prevSanitizedSearchRef.current = sanitizedSearch;
          return;
        }

        fetchVehicles(params, { persistAsBase: true });
        setHasLoadedBase(true);
        prevSanitizedSearchRef.current = sanitizedSearch;
        return;
      }

      // Si el search fue borrado y no hay otros filtros, resetear a base
      if (searchWasCleared && !filtersPayload) {
        resetVehiclesToBase();
        prevSanitizedSearchRef.current = sanitizedSearch;
        return;
      }

      prevSanitizedSearchRef.current = sanitizedSearch;
      return;
    }

    fetchVehicles(params, { persistAsBase: false });
    prevSanitizedSearchRef.current = sanitizedSearch;
  }, [
    contactId,
    page,
    pageSize,
    orderBy,
    orderDirection,
    filtersPayload,
    sanitizedSearch,
    fetchVehicles,
    hasLoadedBase,
    baseVehicles.length,
    resetVehiclesToBase,
  ]);

  // ============================================================================
  // Handlers - Filter Handlers
  // ============================================================================
  const handleMakeChange = useCallback((value) => {
    const processedValue = Array.isArray(value) ? value[0] : value;
    setMakeFilter(processedValue || "");
    setModelFilter("");
    setPage(1);
  }, []);

  const handleModelChange = useCallback((value) => {
    const processedValue = Array.isArray(value) ? value[0] : value;
    setModelFilter(processedValue || "");
    setPage(1);
  }, []);

  const handleYearChange = useCallback((value) => {
    const processedValue = Array.isArray(value) ? value[0] : value;
    setYearFilter(processedValue || "");
    setPage(1);
  }, []);

  // ============================================================================
  // Handlers - Search Handlers
  // ============================================================================
  const handleSearchChange = useCallback((value) => {
    setSearchValue(value);
  }, []);

  // ============================================================================
  // Handlers - Clear Handlers
  // ============================================================================
  const handleClearFilters = useCallback(() => {
    setSearchValue("");
    setMakeFilter("");
    setModelFilter("");
    setYearFilter("");
    setPage(1);
    resetVehiclesToBase();
  }, [resetVehiclesToBase]);

  const handleClearFiltersWithKeys = useCallback(() => {
    resetLocalSearches();
    setSearchKey((prev) => prev + 1);
    setFiltersKey((prev) => prev + 1);
    handleClearFilters();
  }, [resetLocalSearches, handleClearFilters]);

  // ============================================================================
  // Handlers - Pagination Handlers
  // ============================================================================
  const handlePageChange = useCallback(
    (nextPage) => {
      setPage(nextPage);

      if (nextPage === 1) {
        const hasFilters = Boolean(
          makeFilter ||
            modelFilter ||
            yearFilter ||
            sanitizedSearch
        );
        const isDefaultSorting =
          (orderBy[0] || "") === "createdAt" && (orderDirection[0] || "") === "DESC";

        if (!hasFilters && isDefaultSorting) {
          resetVehiclesToBase();
        }
      }
    },
    [
      makeFilter,
      modelFilter,
      yearFilter,
      sanitizedSearch,
      orderBy,
      orderDirection,
      resetVehiclesToBase,
    ]
  );

  const handleItemsPerPageChange = useCallback(
    (itemsPerPage) => {
      setPageSize(itemsPerPage);
      setPage(1);

      if (itemsPerPage === 20) {
        const hasFilters = Boolean(
          makeFilter ||
            modelFilter ||
            yearFilter ||
            sanitizedSearch
        );
        const isDefaultSorting =
          (orderBy[0] || "") === "createdAt" && (orderDirection[0] || "") === "DESC";

        if (!hasFilters && isDefaultSorting) {
          resetVehiclesToBase();
        }
      }
    },
    [
      makeFilter,
      modelFilter,
      yearFilter,
      sanitizedSearch,
      orderBy,
      orderDirection,
      resetVehiclesToBase,
    ]
  );

  // ============================================================================
  // Handlers - Refresh Handler
  // ============================================================================
  const handleRefresh = useCallback(() => {
    if (!contactId) return;
    
    const params = {
      party_uuid: contactId,
      page,
      page_size: pageSize,
      order_by: orderBy,
      order_direction: orderDirection,
      filters: filtersPayload,
      search: sanitizedSearch ? { keyword: sanitizedSearch } : undefined,
    };
    
    fetchVehicles(params);
  }, [
    contactId,
    page,
    pageSize,
    orderBy,
    orderDirection,
    filtersPayload,
    sanitizedSearch,
    fetchVehicles,
  ]);

  // ============================================================================
  // Return
  // ============================================================================
  return {
    contactId,
    loading,
    filtersLoading,
    error,
    isEmpty,
    vehicles,
    vehiclesMeta,
    filters: {
      makeOptions,
      modelOptions,
      yearOptions,
      filteredMake,
      filteredModel,
      filteredYear,
      selectedMake: makeFilter,
      selectedModel: modelFilter,
      selectedYear: yearFilter,
      searchValue,
      hasActiveFilters,
    },
    filterHandlers: {
      handleMakeChange,
      handleModelChange,
      handleYearChange,
      handleSearchChange,
      handleClearFiltersWithKeys,
    },
    filterUI: {
      searchKey,
      filtersKey,
      updateLocalSearch,
    },
    handlePageChange,
    handleItemsPerPageChange,
    handleRefresh,
  };
};
